"use client"

import React, { useState, useMemo } from 'react'
import FormTextInput from '@/shared/components/form-inputs/form-text-input'
import FormTextareaInput from '@/shared/components/form-inputs/form-textarea-input'
import FormOrganizationInput from '@/shared/components/form-inputs/form-organization-input'
import FormPhoneInput from '@/shared/components/form-inputs/form-phone-input'
import FormSelectInput from '@/shared/components/form-inputs/form-select-input'
import FormYesNoInput from '@/shared/components/form-inputs/form-yes-no-input'
import FormDateInput from '@/shared/components/form-inputs/form-date-input'
import FormNumberRangeInput from '@/shared/components/form-inputs/form-number-range-input'
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/shared/components/ui/form'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import { DialogClose } from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { useFormContext, useController, useFieldArray, useWatch } from 'react-hook-form'
import { UpdateUserFormInputType } from '../../schemas/update-user.schema'
import { ROLE_DESCRIPTIONS } from '@/shared/constants/user-roles'
import FormDateRangeInput from '@/shared/components/form-inputs/form-date-range-input'
import { StateCountryAutocomplete } from '@/shared/components/form-inputs/StateCountryAutocomplete'
import { genderOptions, tShirtSizeOptions } from '@/features/profile/schemas/profile-form.schema'
import { otherLanguageOptions } from '@/features/auth/constants/select-options'
import { Input } from '@/shared/components/ui/new-input'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/components/ui/accordion'
import { Plus, X, ChevronDown } from 'lucide-react'
import UpdateUserProfileImage from '../../components/update-user-profile-image'
import { Textarea } from '@/shared/components/ui/textarea'
import { cn } from '@/shared/lib/utils'
import { useRegionOptions } from '@/shared/hooks/use-region-options'
import { getDisplayEmailValue, isNoEmailPlaceholderValue } from '../../schemas/volunteers.schema'
import NoEmailInfoIcon from '@/shared/components/no-email-info-icon'
import {
    sortServePeriodIndicesChronologically,
    findRelevantServePeriodFieldIndexForHighlight,
    hasActiveOrFutureServePeriodForRegion,
} from '../../lib/serve-periods-display'
import { JAMAICA_VOLUNTEER_REGION_ID } from '@/shared/constants/volunteer-region-ids'
import {
    TravelInsuranceReminderCopy,
    VolunteerTravelSupplement,
} from '@/features/profile/components/forms/volunteer-travel-supplement'
import { usePiiAccess, isTier2Masked } from '@/shared/hooks/use-pii-access'
import { RestrictedPiiField } from '@/shared/components/pii/restricted-pii-field'
import { RevealablePiiField } from '@/shared/components/pii/revealable-pii-field'
import { RestrictedPiiSection } from '@/shared/components/pii/restricted-pii-section'
import { isMaskedValue } from '@/shared/lib/pii-utils'

const EXPERIENCE_SKILLS_ACCORDION_VALUE = 'experience-skills'
const VEHICLE_ACCORDION_VALUE = 'vehicle'
const ACCORDION_REQUIRED = 'required'
const ACCORDION_SERVE_DATES = 'serve-dates'
const ACCORDION_ADDITIONAL = 'additional'
const ALL_MAIN_ACCORDION_VALUES = [ACCORDION_REQUIRED, ACCORDION_SERVE_DATES, ACCORDION_ADDITIONAL] as const
const numberRangeInputWrapperClassName = 'md:col-span-1'

/** Calculate age in years from date of birth; returns null if invalid or future date. */
function getAgeFromDateOfBirth(date: Date | undefined | null): number | null {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null
    const today = new Date()
    if (date > today) return null
    let age = today.getFullYear() - date.getFullYear()
    const monthDiff = today.getMonth() - date.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age -= 1
    return age >= 0 ? age : null
}

type UserBaseFormProps = {
    isUpdate?: boolean
    /** Allow editing Email in update mode only when existing stored email was a no-email placeholder */
    canEditEmailOnUpdate?: boolean
    /** `volunteers.regionId` when the update dialog opened; combined with selected regions for Jamaica-only fields */
    initialVolunteerRegionId?: number | null
    /** Update Volunteer (admin): keep travel/Jamaica fields but omit long instructional copy */
    compactTravelUi?: boolean
    /** Raw user/volunteer data from the API — used to detect PII access level. */
    rawUserData?: Record<string, unknown> | null
}


function EmailUpdatesCheckbox({ control }: { control: any }) {
    const { field } = useController({
        control,
        name: "emailUpdates",
    })

    return (
        <div className="flex items-center gap-3">
            <Checkbox
                id="emailUpdates"
                checked={field.value ?? true}
                onCheckedChange={(checked) => {
                    field.onChange(checked === true)
                }}
            />
            <Label htmlFor="emailUpdates" className="text-sm cursor-pointer">
                I want to stay updated
            </Label>
        </div>
    )
}

function UserBaseForm({
    isUpdate = false,
    canEditEmailOnUpdate = false,
    initialVolunteerRegionId = null,
    compactTravelUi = false,
    rawUserData = null,
}: UserBaseFormProps) {
    const { formState: { isSubmitting, isDirty }, control, watch, setValue, getValues } = useFormContext<UpdateUserFormInputType>()

    // Detect PII access level from the raw API response data
    const piiAccess = usePiiAccess(rawUserData)
    const volunteerId = (rawUserData as any)?.volunteer?.id ?? (rawUserData as any)?.volunteerId
    const isDisabled = isUpdate 
        ? (!isDirty || isSubmitting)
        : isSubmitting

    const [locationDetails, setLocationDetails] = useState<{ city: string; state: string; country: string; latitude?: number; longitude?: number; zipCode?: string } | null>(null)
    const [mainAccordionValue, setMainAccordionValue] = useState<string[]>([...ALL_MAIN_ACCORDION_VALUES])
    const [experienceSkillsAccordionValue, setExperienceSkillsAccordionValue] = useState<string[]>([EXPERIENCE_SKILLS_ACCORDION_VALUE])
    const [vehicleAccordionValue, setVehicleAccordionValue] = useState<string[]>([VEHICLE_ACCORDION_VALUE])
    const [whatElseExpanded, setWhatElseExpanded] = useState(false)
    const [isEmailEditingOnUpdate, setIsEmailEditingOnUpdate] = useState(false)
    const [emailValueBeforeEdit, setEmailValueBeforeEdit] = useState('')
    const skillsAndQualitiesField = useController({ control, name: "skillsAndQualities" })
    const allSectionsOpen = mainAccordionValue.length === ALL_MAIN_ACCORDION_VALUES.length
        && experienceSkillsAccordionValue.length === 1
        && vehicleAccordionValue.length === 1
    const handleExpandCollapseAll = () => {
        if (allSectionsOpen) {
            setMainAccordionValue([])
            setExperienceSkillsAccordionValue([])
            setVehicleAccordionValue([])
        } else {
            setMainAccordionValue([...ALL_MAIN_ACCORDION_VALUES])
            setExperienceSkillsAccordionValue([EXPERIENCE_SKILLS_ACCORDION_VALUE])
            setVehicleAccordionValue([VEHICLE_ACCORDION_VALUE])
        }
    }
    const { fields, append, remove } = useFieldArray({
        control,
        name: "servePeriods",
    })

    const watchedServePeriods = useWatch({ control, name: "servePeriods" })
    const sortedServeFieldIndices = useMemo(
        () =>
            sortServePeriodIndicesChronologically(fields.length, (i) => ({
                from: watchedServePeriods?.[i]?.from,
                to: watchedServePeriods?.[i]?.to,
            })),
        [fields.length, watchedServePeriods]
    )
    const relevantServeFieldIndex = useMemo(
        () =>
            findRelevantServePeriodFieldIndexForHighlight(sortedServeFieldIndices, (i) => ({
                from: watchedServePeriods?.[i]?.from,
                to: watchedServePeriods?.[i]?.to,
            })),
        [sortedServeFieldIndices, watchedServePeriods]
    )

    const { options: activeRegionOptions, isLoading: regionsLoading } = useRegionOptions(true)

    // Filter out homeowner role from options
    const roleOptions = ROLE_DESCRIPTIONS
        .filter(role => role.name !== 'Homeowner')
        .map(role => ({
            label: role.name,
            value: role.id.toString(),
        }))

    const statusOptions = [
        { label: 'Active', value: 'Active' },
        { label: 'Inactive', value: 'Inactive' },
    ]

    const yesNoOptions = [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
    ]

    const watchedSelectedRegions = useWatch({ control, name: 'selectedRegions' })
    const showJamaicaVolunteerTravel = useMemo(() => {
        if (
            hasActiveOrFutureServePeriodForRegion(
                watchedServePeriods as Array<{ from?: unknown; to?: unknown; regionId?: unknown }>,
                JAMAICA_VOLUNTEER_REGION_ID
            )
        ) {
            return true
        }
        const wr = watchedSelectedRegions
        if (Array.isArray(wr) && wr.length > 0) {
            const n = parseInt(String(wr[0]), 10)
            if (!isNaN(n)) return n === JAMAICA_VOLUNTEER_REGION_ID
        }
        return initialVolunteerRegionId === JAMAICA_VOLUNTEER_REGION_ID
    }, [watchedSelectedRegions, watchedServePeriods, initialVolunteerRegionId])

    return (
        <div className='grid grid-cols-1 gap-5 md:grid-cols-2'>
            <div className="md:col-span-2">
                <UpdateUserProfileImage />
            </div>
            {/* Required, Serve Dates, Additional information – accordion cards like Profile */}
            <div className="md:col-span-2">
                <div className="flex justify-end mb-2">
                    <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={handleExpandCollapseAll}
                        className="bg-primary hover:bg-primary/90 text-white font-bold"
                    >
                        {allSectionsOpen ? 'Collapse All' : 'Expand All'}
                    </Button>
                </div>
                <Accordion
                    type="multiple"
                    value={mainAccordionValue}
                    onValueChange={setMainAccordionValue}
                    className="space-y-3 w-full"
                >
                    <AccordionItem value={ACCORDION_REQUIRED} className="border rounded-lg bg-muted p-4 px-4">
                        <AccordionTrigger className="py-0 p-1" downChevronIconClassName="w-5 h-5">
                            <div className="text-left">
                                <h2 className="text-xl font-semibold">Required Information</h2>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-0">
                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-3">
                                <FormTextInput
                                    name='fullname'
                                    inputGroupProps={{ label: 'Full Name *' }}
                                    placeholder='Full Name'
                                    control={control}
                                />
                                <FormPhoneInput
                                    name='phone'
                                    placeholder='Phone Number'
                                    inputGroupProps={{ label: 'Phone Number *' }}
                                    control={control}
                                />
                                <div className="md:col-span-2 flex flex-col gap-1">
                                    {isUpdate && !isEmailEditingOnUpdate ? (
                                        <FormField
                                            control={control}
                                            name="email"
                                            render={({ field, fieldState }) => (
                                                <FormItem>
                                                    <FormLabel className="text-foreground">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span>Email *</span>
                                                            {!isEmailEditingOnUpdate ? (
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="h-6 px-2 text-xs"
                                                                    onClick={() => {
                                                                        const currentEmail = String(getValues('email') ?? '')
                                                                        setEmailValueBeforeEdit(currentEmail)
                                                                        setIsEmailEditingOnUpdate(true)
                                                                        if (
                                                                            canEditEmailOnUpdate ||
                                                                            isNoEmailPlaceholderValue(currentEmail)
                                                                        ) {
                                                                            setValue('email', '', {
                                                                                shouldDirty: false,
                                                                                shouldValidate: true,
                                                                            })
                                                                        }
                                                                    }}
                                                                >
                                                                    Edit email
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="h-6 px-2 text-xs"
                                                                    onClick={() => {
                                                                        setValue('email', emailValueBeforeEdit, {
                                                                            shouldDirty: false,
                                                                            shouldValidate: true,
                                                                        })
                                                                        setIsEmailEditingOnUpdate(false)
                                                                    }}
                                                                >
                                                                    Cancel email edit
                                                                </Button>
                                                            )}
                                                            <NoEmailInfoIcon />
                                                            <EmailUpdatesCheckbox control={control} />
                                                        </div>
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="text"
                                                            value={getDisplayEmailValue(String(field.value ?? ""))}
                                                            readOnly
                                                            disabled
                                                            className="bg-muted"
                                                        />
                                                    </FormControl>
                                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                                </FormItem>
                                            )}
                                        />
                                    ) : (
                                        <div className="space-y-2">
                                            <FormTextInput
                                                name='email'
                                                placeholder={isUpdate ? 'Enter email' : 'Email'}
                                                type='email'
                                                disabled={false}
                                                inputGroupProps={{
                                                    label: (
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span>Email *</span>
                                                            {isUpdate && (
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="h-6 px-2 text-xs"
                                                                    onClick={() => {
                                                                        setValue('email', emailValueBeforeEdit, {
                                                                            shouldDirty: false,
                                                                            shouldValidate: true,
                                                                        })
                                                                        setIsEmailEditingOnUpdate(false)
                                                                    }}
                                                                >
                                                                    Cancel email edit
                                                                </Button>
                                                            )}
                                                            <NoEmailInfoIcon />
                                                            <EmailUpdatesCheckbox control={control} />
                                                        </div>
                                                    ),
                                                }}
                                                control={control}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <StateCountryAutocomplete
                                        setLocationDetails={setLocationDetails}
                                        locationDetails={locationDetails}
                                        control={control}
                                        id="1"
                                        highlightWhenEmpty={false}
                                    />
                                </div>
                                <FormOrganizationInput
                                    name='organizationName'
                                    placeholder='Search or type organization name...'
                                    inputGroupProps={{ label: 'Organization/Church Name or Individual *' }}
                                    control={control}
                                />
                                {/* Date of Birth — Tier 2 PII (masked to year only for leaders) */}
                                {isUpdate && rawUserData && !piiAccess.isSelf && !piiAccess.hasTier2 ? (
                                    <>
                                        <RestrictedPiiField label="Date of Birth *" />
                                        <RestrictedPiiField label="Age" />
                                    </>
                                ) : isUpdate && rawUserData && !piiAccess.isSelf && piiAccess.hasTier2 && isTier2Masked(rawUserData, 'dateOfBirth') ? (
                                    <>
                                        <RevealablePiiField
                                            label="Date of Birth"
                                            maskedValue={rawUserData?.dateOfBirth as string | null}
                                            volunteerId={volunteerId}
                                            fieldName="dateOfBirth"
                                        />
                                        <FormItem className="space-y-0.5 w-full flex flex-col">
                                            <FormLabel className="text-foreground">Age (approx)</FormLabel>
                                            <FormControl>
                                                <Input
                                                    readOnly
                                                    disabled
                                                    value={(() => {
                                                        const dobStr = rawUserData?.dateOfBirth as string | null
                                                        const yearMatch = dobStr?.match(/\d{4}/)
                                                        if (yearMatch) {
                                                            return `~${new Date().getFullYear() - parseInt(yearMatch[0], 10)} years`
                                                        }
                                                        return ''
                                                    })()}
                                                    placeholder="—"
                                                    className="h-9 min-h-9 bg-muted"
                                                />
                                            </FormControl>
                                        </FormItem>
                                    </>
                                ) : (
                                    <>
                                        <FormDateInput
                                            name='dateOfBirth'
                                            inputGroupProps={{ label: 'Date of Birth *' }}
                                            placeholder='Select date of birth'
                                            control={control}
                                            enableYearMonthDropdowns={true}
                                            fromYear={new Date().getFullYear() - 100}
                                            toYear={new Date().getFullYear()}
                                        />
                                        <FormItem className="space-y-0.5 w-full flex flex-col">
                                            <FormLabel className="text-foreground">Age</FormLabel>
                                            <FormControl>
                                                <Input
                                                    readOnly
                                                    disabled
                                                    value={(() => {
                                                        const age = getAgeFromDateOfBirth(watch('dateOfBirth'))
                                                        return age != null ? `${age} years` : ''
                                                    })()}
                                                    placeholder="—"
                                                    className="h-9 min-h-9 bg-muted"
                                                />
                                            </FormControl>
                                        </FormItem>
                                    </>
                                )}
                                {/* Gender — Tier 1 PII */}
                                {isUpdate && rawUserData && !piiAccess.isSelf && !piiAccess.hasTier1 ? (
                                    <RestrictedPiiField label="Gender *" />
                                ) : (
                                    <FormSelectInput
                                        name='gender'
                                        inputGroupProps={{ label: 'Gender *' }}
                                        placeholder="Select for lodging logistics"
                                        options={genderOptions}
                                        control={control}
                                    />
                                )}
                                <FormSelectInput
                                    name='role'
                                    inputGroupProps={{ label: 'Role' }}
                                    placeholder='Select Role'
                                    options={roleOptions}
                                    control={control}
                                />
                                <FormSelectInput
                                    name='status'
                                    inputGroupProps={{ label: 'Status' }}
                                    placeholder='Select Status'
                                    options={statusOptions}
                                    control={control}
                                />
                                <FormSelectInput
                                    name='tShirtSize'
                                    inputGroupProps={{ label: 'T-shirt size' }}
                                    placeholder="Select for potential addon"
                                    options={tShirtSizeOptions}
                                    control={control}
                                />
                                {/* Background Check — Tier 1 PII */}
                                {isUpdate && (
                                    isUpdate && rawUserData && !piiAccess.isSelf && !piiAccess.hasTier1 ? (
                                        <RestrictedPiiField label="Background check" />
                                    ) : (
                                        <FormDateInput
                                            name="backgroundCheck"
                                            inputGroupProps={{ label: 'Background check' }}
                                            placeholder="Select date"
                                            control={control}
                                            enableYearMonthDropdowns={true}
                                            fromYear={new Date().getFullYear() - 30}
                                            toYear={new Date().getFullYear() + 1}
                                        />
                                    )
                                )}
                                {/* Health & Dietary — Tier 1 PII */}
                                {isUpdate && rawUserData && !piiAccess.isSelf && !piiAccess.hasTier1 ? (
                                    <div className="md:col-span-2 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-3">
                                        <RestrictedPiiField label="Health conditions" />
                                        <RestrictedPiiField label="Dietary restrictions" />
                                    </div>
                                ) : (
                                    <div className="md:col-span-2 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-3">
                                        <FormTextareaInput
                                            name='healthConditions'
                                            inputGroupProps={{ label: 'Health conditions' }}
                                            placeholder='Any health conditions we should know about'
                                            control={control}
                                        />
                                        <FormTextareaInput
                                            name='dietaryRestrictions'
                                            inputGroupProps={{ label: 'Dietary restrictions' }}
                                            placeholder='Any dietary restrictions or allergies'
                                            control={control}
                                        />
                                    </div>
                                )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value={ACCORDION_SERVE_DATES} className="border rounded-lg bg-muted p-4 px-4">
                        <AccordionTrigger className="py-0 p-1" downChevronIconClassName="w-5 h-5">
                            <div className="text-left">
                                <h2 className="text-xl font-semibold">Serve Dates</h2>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-0">
                            <div className="space-y-4">
                                <div className="space-y-3">
                                    {sortedServeFieldIndices.map((index, displayPos) => {
                                        const field = fields[index]
                                        const displayIdx = displayPos + 1
                                        const isCurrentPeriod = relevantServeFieldIndex === index
                                        const arrivalLabel =
                                            displayIdx === 1
                                                ? 'Serve Arrival and Departure Dates'
                                                : `Serve Period ${displayIdx}`
                                        return (
                                        <div
                                            key={field.id}
                                            className={cn(
                                                "flex flex-col gap-3 sm:flex-row sm:items-start rounded-lg border p-3 -m-1 transition-colors",
                                                isCurrentPeriod
                                                    ? "border-emerald-300/90 bg-emerald-50/90 dark:border-emerald-800/60 dark:bg-emerald-950/35"
                                                    : "border-transparent"
                                            )}
                                        >
                                            <div className="flex-1 grid grid-cols-1 gap-3 sm:grid-cols-[2fr,1fr] min-w-0">
                                                <FormDateRangeInput
                                                    control={control}
                                                    inputGroupProps={{
                                                        label:
                                                            fields.length > 1
                                                                ? (
                                                                    <div className="flex items-center justify-between w-full gap-2">
                                                                        <span>{arrivalLabel}</span>
                                                                        <Button
                                                                            type="button"
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            onClick={() => fields.length > 1 && remove(index)}
                                                                            className="shrink-0 text-destructive hover:text-destructive md:hidden"
                                                                            aria-label={
                                                                                displayIdx === 1
                                                                                    ? 'Remove serve period'
                                                                                    : `Remove serve period ${displayIdx}`
                                                                            }
                                                                        >
                                                                            <X className="h-4 w-4" />
                                                                        </Button>
                                                                    </div>
                                                                )
                                                                : arrivalLabel,
                                                    }}
                                                    name={`servePeriods.${index}`}
                                                />
                                                <div className="min-w-0">
                                                    <FormSelectInput
                                                        name={`servePeriods.${index}.regionId`}
                                                        inputGroupProps={{ label: 'Region' }}
                                                        placeholder={regionsLoading ? "Loading regions..." : "Select region..."}
                                                        options={activeRegionOptions}
                                                        control={control}
                                                        loading={regionsLoading}
                                                    />
                                                </div>
                                            </div>
                                            {fields.length > 1 && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => {
                                                        if (fields.length > 1) remove(index)
                                                    }}
                                                    className="mt-8 sm:mt-8 text-destructive hover:text-destructive shrink-0 hidden md:flex"
                                                    aria-label={
                                                        displayIdx === 1
                                                            ? 'Remove serve period'
                                                            : `Remove serve period ${displayIdx}`
                                                    }
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                        )
                                    })}
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => append({ from: undefined, to: undefined, regionId: undefined })}
                                    className="w-full sm:w-auto"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add another serve period
                                </Button>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value={ACCORDION_ADDITIONAL} className="border rounded-lg bg-muted p-4 px-4">
                        <AccordionTrigger className="py-0 p-1" downChevronIconClassName="w-5 h-5">
                            <div className="text-left">
                                <h2 className="text-xl font-semibold">Travel Information</h2>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-0">
                            {/* Show restricted section banner when user has no PII access and is not viewing their own data */}
                            {isUpdate && rawUserData && !piiAccess.isSelf && !piiAccess.hasTier1 ? (
                                <div className="space-y-4">
                                    <RestrictedPiiSection sectionName="Travel Information" />
                                    {/* Still show non-PII travel fields */}
                                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-3">
                                        <div className="md:col-span-2 w-full space-y-5">
                                            <VolunteerTravelSupplement
                                                control={control}
                                                showJamaicaSections={showJamaicaVolunteerTravel}
                                                compact={compactTravelUi}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-3">
                                {/* Passport — Tier 2: show revealable for admin, restricted for others without access */}
                                {isUpdate && rawUserData && !piiAccess.isSelf && !piiAccess.hasTier2 ? (
                                    <RestrictedPiiField label="Passport number" />
                                ) : isUpdate && rawUserData && !piiAccess.isSelf && piiAccess.hasTier2 && isTier2Masked(rawUserData, 'passportNumber') ? (
                                    <RevealablePiiField
                                        label="Passport number"
                                        maskedValue={rawUserData?.passportNumber as string | null}
                                        volunteerId={volunteerId}
                                        fieldName="passportNumber"
                                    />
                                ) : (
                                    <FormTextInput
                                        name='passportNumber'
                                        inputGroupProps={{ label: 'Passport number' }}
                                        placeholder='Passport number'
                                        control={control}
                                    />
                                )}
                                {/* Emergency contacts — Tier 1 */}
                                {isUpdate && rawUserData && !piiAccess.isSelf && !piiAccess.hasTier1 ? (
                                    <>
                                        <RestrictedPiiField label="Emergency contact 1 – Full name" />
                                        <RestrictedPiiField label="Emergency contact 1 – Relationship" />
                                        <RestrictedPiiField label="Emergency contact 1 – Phone" />
                                        <RestrictedPiiField label="Emergency contact 2 – Full name" />
                                        <RestrictedPiiField label="Emergency contact 2 – Relationship" />
                                        <RestrictedPiiField label="Emergency contact 2 – Phone" />
                                    </>
                                ) : (
                                    <>
                                        <FormTextInput
                                            name='emergencyContact1FullName'
                                            inputGroupProps={{ label: 'Emergency contact 1 – Full name' }}
                                            placeholder='Full name'
                                            control={control}
                                        />
                                        <FormTextInput
                                            name='emergencyContact1Relationship'
                                            inputGroupProps={{ label: 'Emergency contact 1 – Relationship' }}
                                            placeholder='e.g. Spouse, Parent'
                                            control={control}
                                        />
                                        <FormPhoneInput
                                            name='emergencyContact1Phone'
                                            inputGroupProps={{ label: 'Emergency contact 1 – Phone' }}
                                            placeholder='Phone'
                                            control={control}
                                        />
                                        <FormTextInput
                                            name='emergencyContact2FullName'
                                            inputGroupProps={{ label: 'Emergency contact 2 – Full name' }}
                                            placeholder='Full name'
                                            control={control}
                                        />
                                        <FormTextInput
                                            name='emergencyContact2Relationship'
                                            inputGroupProps={{ label: 'Emergency contact 2 – Relationship' }}
                                            placeholder='e.g. Spouse, Parent'
                                            control={control}
                                        />
                                        <FormPhoneInput
                                            name='emergencyContact2Phone'
                                            inputGroupProps={{ label: 'Emergency contact 2 – Phone' }}
                                            placeholder='Phone'
                                            control={control}
                                        />
                                    </>
                                )}
                                {/* Health insurance — Provider/phone are not PII, policy # is Tier 2 */}
                                <div className="md:col-span-2 grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-3">
                                    <FormTextInput
                                        name='healthInsuranceProvider'
                                        inputGroupProps={{ label: 'Health insurance – Provider' }}
                                        placeholder='Provider name'
                                        control={control}
                                    />
                                    <FormPhoneInput
                                        name='healthInsurancePhone'
                                        inputGroupProps={{ label: 'Health insurance – Phone' }}
                                        placeholder='Phone'
                                        control={control}
                                    />
                                    {isUpdate && rawUserData && !piiAccess.isSelf && !piiAccess.hasTier2 ? (
                                        <RestrictedPiiField label="Health insurance – Policy number" />
                                    ) : isUpdate && rawUserData && !piiAccess.isSelf && piiAccess.hasTier2 && isTier2Masked(rawUserData, 'healthInsurancePolicyNumber') ? (
                                        <RevealablePiiField
                                            label="Health insurance – Policy number"
                                            maskedValue={rawUserData?.healthInsurancePolicyNumber as string | null}
                                            volunteerId={volunteerId}
                                            fieldName="healthInsurancePolicyNumber"
                                        />
                                    ) : (
                                        <FormTextInput
                                            name='healthInsurancePolicyNumber'
                                            inputGroupProps={{ label: 'Health insurance – Policy number' }}
                                            placeholder='Policy number'
                                            control={control}
                                        />
                                    )}
                                </div>
                                {!compactTravelUi && (
                                    <div className="md:col-span-2 w-full">
                                        <TravelInsuranceReminderCopy />
                                    </div>
                                )}
                                {/* Travel insurance — same pattern: Provider/phone not PII, policy # is Tier 2 */}
                                <div className="md:col-span-2 grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-3">
                                    <FormTextInput
                                        name='travelInsuranceProvider'
                                        inputGroupProps={{ label: 'Travel insurance – Provider' }}
                                        placeholder='Provider name'
                                        control={control}
                                    />
                                    <FormPhoneInput
                                        name='travelInsurancePhone'
                                        inputGroupProps={{ label: 'Travel insurance – Phone' }}
                                        placeholder='Phone'
                                        control={control}
                                    />
                                    {isUpdate && rawUserData && !piiAccess.isSelf && !piiAccess.hasTier2 ? (
                                        <RestrictedPiiField label="Travel insurance – Policy number" />
                                    ) : isUpdate && rawUserData && !piiAccess.isSelf && piiAccess.hasTier2 && isTier2Masked(rawUserData, 'travelInsurancePolicyNumber') ? (
                                        <RevealablePiiField
                                            label="Travel insurance – Policy number"
                                            maskedValue={rawUserData?.travelInsurancePolicyNumber as string | null}
                                            volunteerId={volunteerId}
                                            fieldName="travelInsurancePolicyNumber"
                                        />
                                    ) : (
                                        <FormTextInput
                                            name='travelInsurancePolicyNumber'
                                            inputGroupProps={{ label: 'Travel insurance – Policy number' }}
                                            placeholder='Policy number'
                                            control={control}
                                        />
                                    )}
                                </div>
                                <div className="md:col-span-2 w-full space-y-5">
                                    <VolunteerTravelSupplement
                                        control={control}
                                        showJamaicaSections={showJamaicaVolunteerTravel}
                                        compact={compactTravelUi}
                                    />
                                </div>
                            </div>
                            )}
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>

            {/* Experience & Skills – accordion card like Profile */}
            <div className="md:col-span-2">
                <Accordion type="multiple" value={experienceSkillsAccordionValue} onValueChange={setExperienceSkillsAccordionValue} className="w-full">
                    <AccordionItem
                        value={EXPERIENCE_SKILLS_ACCORDION_VALUE}
                        className="bg-muted rounded p-4 border flex flex-col gap-3 transition-colors duration-200"
                    >
                        <AccordionTrigger className="py-0 p-1" downChevronIconClassName="w-5 h-5">
                            <div className="text-left">
                                <h2 className="text-xl font-semibold">Experience &amp; Skills</h2>
                                <p className="text-sm font-normal text-muted-foreground mt-0.5">
                                    (1 = Not Experienced/Interested, 5 = Very Experienced/Interested)
                                </p>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-0 p-1">
                            <div className="grid grid-cols-1 gap-5 items-start md:grid-cols-2 md:gap-4 md:pb-2 xl:grid-cols-2 xl:gap-x-8 xl:gap-y-6">
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with general construction?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceConstruction"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How interested are you in manual labor?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceManualLabor"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How interested are you in leading at a work site?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceLeadingTeams"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with drywall finishing?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceDrywallFinishing"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with rough carpentry such as framing and laying subfloor?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceRoughCarpentry"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with finish carpentry such as installing trim, doors, or cabinets?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceFinishCarpentry"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with flooring such as laminate vinyl plank flooring?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceFlooring"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with woodworking?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceWoodWorking"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with electrical work with 5 being a Journeyman and up?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceElectrical"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with plumbing with 5 being a Journeyman and up?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experiencePlumbing"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with HVAC with 5 being a Journeyman and up?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceHvac"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with arborist/tree work with 5 being certified?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceArborist"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with roofing with 5 being certified?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceRoofing"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with heavy equipment with 5 being certified?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceHeavyEquipment"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with google programs/data management?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceDataManagement"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How experienced are you with storytelling/social media?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceStorytelling"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How interested are you in Alternative Ministry opportunities, such as visiting the nursing home, kid's mentorship, or food/supply distribution events?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="experienceAltMinistries"
                                    control={control}
                                />
                                <FormNumberRangeInput
                                    inputGroupProps={{ label: "How interested would you be in cooking meals for all of the groups/volunteers during the week?", wrapperClassName: numberRangeInputWrapperClassName }}
                                    name="mealPreparationAbility"
                                    control={control}
                                />
                                <div className="col-span-full grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-3 md:items-end">
                                    <FormSelectInput
                                        inputGroupProps={{ label: "Do you speak any other language besides English?" }}
                                        placeholder="Select One"
                                        options={otherLanguageOptions}
                                        name="otherLanguages"
                                        control={control}
                                    />
                                    <div className="col-span-full space-y-2">
                                        <div className="flex flex-wrap items-center gap-2 w-full min-w-0">
                                            <FormLabel className="text-sm font-medium min-w-0 flex-1 basis-0 break-words">What else would you like us know concerning your skills, experience or expectations?</FormLabel>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setWhatElseExpanded(!whatElseExpanded)}
                                                className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                                                aria-label={whatElseExpanded ? "Collapse" : "Expand to read more"}
                                            >
                                                <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", whatElseExpanded && "rotate-180")} />
                                            </Button>
                                        </div>
                                        <FormItem>
                                            <FormControl>
                                                <div className={cn("overflow-y-auto rounded-md border border-input bg-background transition-[max-height] duration-200", whatElseExpanded ? "max-h-[320px]" : "max-h-[72px]")}>
                                                    <Textarea
                                                        {...skillsAndQualitiesField.field}
                                                        value={skillsAndQualitiesField.field.value ?? ""}
                                                        onChange={(e) => skillsAndQualitiesField.field.onChange(e.target.value)}
                                                        placeholder="Mention skills or qualities"
                                                        rows={3}
                                                        className="min-h-0 border-0 resize-none focus-visible:ring-0 focus-visible:ring-offset-0 rounded-md"
                                                    />
                                                </div>
                                            </FormControl>
                                            <FormMessage>{skillsAndQualitiesField.fieldState.error?.message}</FormMessage>
                                        </FormItem>
                                    </div>
                                </div>
                                <div className="col-span-full">
                                    <FormYesNoInput
                                        name="hasServedBefore"
                                        inputGroupProps={{
                                            label: "Have you served before in disaster relief?",
                                        }}
                                        placeholder="Select"
                                        control={control}
                                    />
                                </div>
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>

            {/* Vehicle – accordion card like Profile */}
            <div className="md:col-span-2">
                <Accordion type="multiple" value={vehicleAccordionValue} onValueChange={setVehicleAccordionValue} className="w-full">
                    <AccordionItem
                        value={VEHICLE_ACCORDION_VALUE}
                        className="bg-muted rounded p-4 border flex flex-col gap-3 transition-colors duration-200"
                    >
                        <AccordionTrigger className="py-0 p-1" downChevronIconClassName="w-5 h-5">
                            <div className="text-left">
                                <h2 className="text-xl font-semibold">Vehicle</h2>
                                <p className="text-sm font-normal text-muted-foreground mt-0.5">
                                    Tell us about the vehicle you will have on site
                                </p>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-0 p-1">
                            <div className="grid grid-cols-1 gap-5 items-start md:grid-cols-2 md:gap-4 md:pb-2 xl:grid-cols-2 xl:gap-x-8 xl:gap-y-6">
                                <FormTextInput
                                    name="vehicleMake"
                                    inputGroupProps={{ label: 'Make' }}
                                    placeholder="e.g. Toyota, Ford"
                                    control={control}
                                />
                                <FormTextInput
                                    name="vehicleModel"
                                    inputGroupProps={{ label: 'Model' }}
                                    placeholder="e.g. Camry, F-150"
                                    control={control}
                                />
                                <FormTextInput
                                    name="vehicleColor"
                                    inputGroupProps={{ label: 'Color' }}
                                    placeholder="e.g. White, Silver"
                                    control={control}
                                />
                                {/* License plate fields — Tier 1 PII */}
                                {isUpdate && rawUserData && !piiAccess.isSelf && !piiAccess.hasTier1 ? (
                                    <>
                                        <RestrictedPiiField label="License plate number" />
                                        <RestrictedPiiField label="License plate state" />
                                    </>
                                ) : (
                                    <>
                                        <FormTextInput
                                            name="vehicleLicensePlateNumber"
                                            inputGroupProps={{ label: 'License plate number' }}
                                            placeholder="License plate number"
                                            control={control}
                                        />
                                        <FormTextInput
                                            name="vehicleLicensePlateState"
                                            inputGroupProps={{ label: 'License plate state' }}
                                            placeholder="e.g. TN, NC"
                                            control={control}
                                        />
                                    </>
                                )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>

            {/* Form Actions - only when not in update mode (update mode uses sticky save bar) */}
            {!isUpdate && (
                <div className="md:col-span-2 flex justify-end gap-3 pt-4">
                    <DialogClose asChild>
                        <Button variant="outline" type="button">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button 
                        type="submit" 
                        disabled={isDisabled}
                        showLoader={isSubmitting}
                        variant={isDisabled && !isSubmitting ? "secondary" : "default"}
                    >
                        {isSubmitting ? 'Updating...' : 'Update Volunteer'}
                    </Button>
                </div>
            )}

        </div>
    )
}

export default UserBaseForm
